// 주문 내역 번역 상수
import type { Language, Locale } from '@/config/locales';
import type { OrderHistoryTranslations } from './types';
import { DEFAULT_STORE_TIMEZONE } from '@/utils/menuAvailability';

// #522 — Locale 단일 출처 정합 (15언어 전수).
export const orderHistoryTranslations:
  & Record<Language, OrderHistoryTranslations>
  & Record<Exclude<Locale, Language>, OrderHistoryTranslations> = {
  ko: {
    title: '주문 내역',
    noOrders: '주문 내역이 없습니다',
    noOrdersDesc: '첫 주문을 시작해보세요!',
    pickup: '픽업',
    table: '테이블',
    all: '전체',
    items: '개',
    viewReceipt: '영수증',
    orderNumber: '주문번호',
    total: '결제금액',
    completed: '완료',
    taxInvoice: '세금계산서 발급',
    taxInvoiceUnavailable: '세금계산서 불가',
    taxInvoiceCompleted: '세금계산서 완료',
    paymentStatus: {
      unpaid: '미결제',
      paid: '결제완료',
      failed: '결제실패',
      cancelled: '결제취소',
      processing: '처리중',
      refunded: '환불완료',
      partialRefund: '부분환불',
      prepayFailed: '선불 결제실패',
      prepayCancelled: '선불 결제취소',
    },
  },
  es: {
    title: 'Historial de pedidos',
    noOrders: 'No hay pedidos',
    noOrdersDesc: '¡Comienza tu primer pedido!',
    pickup: 'Recogida',
    table: 'Mesa',
    all: 'Todos',
    items: 'artículos',
    viewReceipt: 'Recibo',
    orderNumber: 'Número',
    total: 'Total',
    completed: 'Completado',
    taxInvoice: 'Emitir factura',
    taxInvoiceUnavailable: 'Factura no disponible',
    taxInvoiceCompleted: 'Factura emitida',
    paymentStatus: {
      unpaid: 'No pagado',
      paid: 'Pagado',
      failed: 'Pago fallido',
      cancelled: 'Pago cancelado',
      processing: 'Procesando',
      refunded: 'Reembolsado',
      partialRefund: 'Reembolso parcial',
      prepayFailed: 'Pago prepago fallido',
      prepayCancelled: 'Pago prepago cancelado',
    },
  },
  en: {
    title: 'Order History',
    noOrders: 'No orders yet',
    noOrdersDesc: 'Start your first order!',
    pickup: 'Pickup',
    table: 'Table',
    all: 'All',
    items: 'items',
    viewReceipt: 'Receipt',
    orderNumber: 'Order #',
    total: 'Total',
    completed: 'Completed',
    taxInvoice: 'Issue Invoice',
    taxInvoiceUnavailable: 'Invoice Unavailable',
    taxInvoiceCompleted: 'Invoice Issued',
    paymentStatus: {
      unpaid: 'Unpaid',
      paid: 'Paid',
      failed: 'Payment failed',
      cancelled: 'Payment cancelled',
      processing: 'Processing',
      refunded: 'Refunded',
      partialRefund: 'Partial refund',
      prepayFailed: 'Prepaid payment failed',
      prepayCancelled: 'Prepaid payment cancelled',
    },
  },
  pt: {
    title: 'Histórico de pedidos',
    noOrders: 'Nenhum pedido',
    noOrdersDesc: 'Faça seu primeiro pedido!',
    pickup: 'Retirada',
    table: 'Mesa',
    all: 'Todos',
    items: 'itens',
    viewReceipt: 'Recibo',
    orderNumber: 'Número',
    total: 'Total',
    completed: 'Concluído',
    taxInvoice: 'Emitir fatura',
    taxInvoiceUnavailable: 'Fatura indisponível',
    taxInvoiceCompleted: 'Fatura emitida',
    paymentStatus: {
      unpaid: 'Não pago',
      paid: 'Pago',
      failed: 'Pagamento falhou',
      cancelled: 'Pagamento cancelado',
      processing: 'Processando',
      refunded: 'Reembolsado',
      partialRefund: 'Reembolso parcial',
      prepayFailed: 'Pagamento antecipado falhou',
      prepayCancelled: 'Pagamento antecipado cancelado',
    },
  },
  fr: {
    title: 'Historique des commandes',
    noOrders: 'Aucune commande',
    noOrdersDesc: 'Passez votre première commande !',
    pickup: 'À emporter',
    table: 'Table',
    all: 'Tout',
    items: 'articles',
    viewReceipt: 'Reçu',
    orderNumber: 'Numéro',
    total: 'Total',
    completed: 'Terminé',
    taxInvoice: 'Émettre une facture',
    taxInvoiceUnavailable: 'Facture indisponible',
    taxInvoiceCompleted: 'Facture émise',
    paymentStatus: {
      unpaid: 'Non payé',
      paid: 'Payé',
      failed: 'Paiement échoué',
      cancelled: 'Paiement annulé',
      processing: 'En cours',
      refunded: 'Remboursé',
      partialRefund: 'Remboursement partiel',
      prepayFailed: 'Prépaiement échoué',
      prepayCancelled: 'Prépaiement annulé',
    },
  },
  zh: {
    title: '订单记录',
    noOrders: '暂无订单',
    noOrdersDesc: '开始您的第一个订单吧！',
    pickup: '自取',
    table: '桌台',
    all: '全部',
    items: '件',
    viewReceipt: '收据',
    orderNumber: '订单号',
    total: '合计',
    completed: '已完成',
    taxInvoice: '开具发票',
    taxInvoiceUnavailable: '无法开具发票',
    taxInvoiceCompleted: '发票已开具',
    paymentStatus: {
      unpaid: '未支付',
      paid: '已支付',
      failed: '支付失败',
      cancelled: '支付已取消',
      processing: '处理中',
      refunded: '已退款',
      partialRefund: '部分退款',
      prepayFailed: '预付款失败',
      prepayCancelled: '预付款已取消',
    },
  },
  ja: {
    title: '注文履歴',
    noOrders: '注文履歴がありません',
    noOrdersDesc: '最初のご注文をどうぞ！',
    pickup: 'テイクアウト',
    table: 'テーブル',
    all: 'すべて',
    items: '点',
    viewReceipt: 'レシート',
    orderNumber: '注文番号',
    total: '合計',
    completed: '完了',
    taxInvoice: '請求書を発行',
    taxInvoiceUnavailable: '請求書発行不可',
    taxInvoiceCompleted: '請求書発行済み',
    paymentStatus: {
      unpaid: '未払い',
      paid: '支払い済み',
      failed: '支払い失敗',
      cancelled: '支払いキャンセル',
      processing: '処理中',
      refunded: '返金済み',
      partialRefund: '一部返金',
      prepayFailed: '前払い失敗',
      prepayCancelled: '前払いキャンセル',
    },
  },
  hi: {
    title: 'ऑर्डर इतिहास',
    noOrders: 'कोई ऑर्डर नहीं',
    noOrdersDesc: 'अपना पहला ऑर्डर शुरू करें!',
    pickup: 'पिकअप',
    table: 'टेबल',
    all: 'सभी',
    items: 'आइटम',
    viewReceipt: 'रसीद',
    orderNumber: 'ऑर्डर नंबर',
    total: 'कुल',
    completed: 'पूर्ण',
    taxInvoice: 'इनवॉइस जारी करें',
    taxInvoiceUnavailable: 'इनवॉइस उपलब्ध नहीं',
    taxInvoiceCompleted: 'इनवॉइस जारी',
    paymentStatus: {
      unpaid: 'भुगतान नहीं हुआ',
      paid: 'भुगतान हो गया',
      failed: 'भुगतान विफल',
      cancelled: 'भुगतान रद्द',
      processing: 'प्रोसेस हो रहा है',
      refunded: 'धनवापसी पूर्ण',
      partialRefund: 'आंशिक धनवापसी',
      prepayFailed: 'प्रीपेड भुगतान विफल',
      prepayCancelled: 'प्रीपेड भुगतान रद्द',
    },
  },
  de: {
    title: 'Bestellverlauf',
    noOrders: 'Keine Bestellungen',
    noOrdersDesc: 'Starten Sie Ihre erste Bestellung!',
    pickup: 'Abholung',
    table: 'Tisch',
    all: 'Alle',
    items: 'Artikel',
    viewReceipt: 'Beleg',
    orderNumber: 'Bestellnummer',
    total: 'Gesamt',
    completed: 'Abgeschlossen',
    taxInvoice: 'Rechnung ausstellen',
    taxInvoiceUnavailable: 'Rechnung nicht verfügbar',
    taxInvoiceCompleted: 'Rechnung ausgestellt',
    paymentStatus: {
      unpaid: 'Nicht bezahlt',
      paid: 'Bezahlt',
      failed: 'Zahlung fehlgeschlagen',
      cancelled: 'Zahlung storniert',
      processing: 'In Bearbeitung',
      refunded: 'Erstattet',
      partialRefund: 'Teilerstattung',
      prepayFailed: 'Vorauszahlung fehlgeschlagen',
      prepayCancelled: 'Vorauszahlung storniert',
    },
  },
  it: {
    title: 'Cronologia ordini',
    noOrders: 'Nessun ordine',
    noOrdersDesc: 'Inizia il tuo primo ordine!',
    pickup: 'Ritiro',
    table: 'Tavolo',
    all: 'Tutti',
    items: 'articoli',
    viewReceipt: 'Ricevuta',
    orderNumber: 'Numero',
    total: 'Totale',
    completed: 'Completato',
    taxInvoice: 'Emetti fattura',
    taxInvoiceUnavailable: 'Fattura non disponibile',
    taxInvoiceCompleted: 'Fattura emessa',
    paymentStatus: {
      unpaid: 'Non pagato',
      paid: 'Pagato',
      failed: 'Pagamento non riuscito',
      cancelled: 'Pagamento annullato',
      processing: 'In elaborazione',
      refunded: 'Rimborsato',
      partialRefund: 'Rimborso parziale',
      prepayFailed: 'Pagamento anticipato non riuscito',
      prepayCancelled: 'Pagamento anticipato annullato',
    },
  },
  ru: {
    title: 'История заказов',
    noOrders: 'Заказов пока нет',
    noOrdersDesc: 'Сделайте свой первый заказ!',
    pickup: 'Самовывоз',
    table: 'Стол',
    all: 'Все',
    items: 'шт.',
    viewReceipt: 'Чек',
    orderNumber: 'Номер заказа',
    total: 'Итого',
    completed: 'Завершён',
    taxInvoice: 'Выставить счёт-фактуру',
    taxInvoiceUnavailable: 'Счёт-фактура недоступна',
    taxInvoiceCompleted: 'Счёт-фактура выставлена',
    paymentStatus: {
      unpaid: 'Не оплачено',
      paid: 'Оплачено',
      failed: 'Ошибка оплаты',
      cancelled: 'Оплата отменена',
      processing: 'Обработка',
      refunded: 'Возврат выполнен',
      partialRefund: 'Частичный возврат',
      prepayFailed: 'Предоплата не прошла',
      prepayCancelled: 'Предоплата отменена',
    },
  },
  ar: {
    title: 'سجل الطلبات',
    noOrders: 'لا توجد طلبات',
    noOrdersDesc: 'ابدأ طلبك الأول!',
    pickup: 'استلام',
    table: 'طاولة',
    all: 'الكل',
    items: 'عناصر',
    viewReceipt: 'الإيصال',
    orderNumber: 'رقم الطلب',
    total: 'الإجمالي',
    completed: 'مكتمل',
    taxInvoice: 'إصدار فاتورة',
    taxInvoiceUnavailable: 'الفاتورة غير متاحة',
    taxInvoiceCompleted: 'تم إصدار الفاتورة',
    paymentStatus: {
      unpaid: 'غير مدفوع',
      paid: 'مدفوع',
      failed: 'فشل الدفع',
      cancelled: 'تم إلغاء الدفع',
      processing: 'قيد المعالجة',
      refunded: 'تم الاسترداد',
      partialRefund: 'استرداد جزئي',
      prepayFailed: 'فشل الدفع المسبق',
      prepayCancelled: 'تم إلغاء الدفع المسبق',
    },
  },
  vi: {
    title: 'Lịch sử đơn hàng',
    noOrders: 'Chưa có đơn hàng',
    noOrdersDesc: 'Hãy bắt đầu đơn hàng đầu tiên của bạn!',
    pickup: 'Mang đi',
    table: 'Bàn',
    all: 'Tất cả',
    items: 'món',
    viewReceipt: 'Biên lai',
    orderNumber: 'Số đơn hàng',
    total: 'Tổng cộng',
    completed: 'Hoàn tất',
    taxInvoice: 'Xuất hóa đơn',
    taxInvoiceUnavailable: 'Không thể xuất hóa đơn',
    taxInvoiceCompleted: 'Đã xuất hóa đơn',
    paymentStatus: {
      unpaid: 'Chưa thanh toán',
      paid: 'Đã thanh toán',
      failed: 'Thanh toán thất bại',
      cancelled: 'Đã hủy thanh toán',
      processing: 'Đang xử lý',
      refunded: 'Đã hoàn tiền',
      partialRefund: 'Hoàn tiền một phần',
      prepayFailed: 'Thanh toán trả trước thất bại',
      prepayCancelled: 'Đã hủy thanh toán trả trước',
    },
  },
  th: {
    title: 'ประวัติการสั่งซื้อ',
    noOrders: 'ยังไม่มีคำสั่งซื้อ',
    noOrdersDesc: 'เริ่มสั่งซื้อครั้งแรกของคุณเลย!',
    pickup: 'รับเอง',
    table: 'โต๊ะ',
    all: 'ทั้งหมด',
    items: 'รายการ',
    viewReceipt: 'ใบเสร็จ',
    orderNumber: 'หมายเลขคำสั่งซื้อ',
    total: 'ยอดรวม',
    completed: 'เสร็จสิ้น',
    taxInvoice: 'ออกใบกำกับภาษี',
    taxInvoiceUnavailable: 'ไม่สามารถออกใบกำกับภาษี',
    taxInvoiceCompleted: 'ออกใบกำกับภาษีแล้ว',
    paymentStatus: {
      unpaid: 'ยังไม่ชำระเงิน',
      paid: 'ชำระเงินแล้ว',
      failed: 'การชำระเงินล้มเหลว',
      cancelled: 'ยกเลิกการชำระเงิน',
      processing: 'กำลังดำเนินการ',
      refunded: 'คืนเงินแล้ว',
      partialRefund: 'คืนเงินบางส่วน',
      prepayFailed: 'การชำระล่วงหน้าล้มเหลว',
      prepayCancelled: 'ยกเลิกการชำระล่วงหน้าแล้ว',
    },
  },
  nl: {
    title: 'Bestelgeschiedenis',
    noOrders: 'Geen bestellingen',
    noOrdersDesc: 'Plaats uw eerste bestelling!',
    pickup: 'Afhalen',
    table: 'Tafel',
    all: 'Alles',
    items: 'artikelen',
    viewReceipt: 'Bon',
    orderNumber: 'Bestelnummer',
    total: 'Totaal',
    completed: 'Voltooid',
    taxInvoice: 'Factuur uitgeven',
    taxInvoiceUnavailable: 'Factuur niet beschikbaar',
    taxInvoiceCompleted: 'Factuur uitgegeven',
    paymentStatus: {
      unpaid: 'Niet betaald',
      paid: 'Betaald',
      failed: 'Betaling mislukt',
      cancelled: 'Betaling geannuleerd',
      processing: 'In behandeling',
      refunded: 'Terugbetaald',
      partialRefund: 'Gedeeltelijke terugbetaling',
      prepayFailed: 'Vooruitbetaling mislukt',
      prepayCancelled: 'Vooruitbetaling geannuleerd',
    },
  },
};

// formatCurrency는 @/utils/currency의 함수를 사용하세요
// 이 함수는 향후 버전에서 제거될 예정입니다

// #522 — 상대 시간 템플릿 (15언어 전수). 기존 ko/es/en 문구는 그대로 보존.
interface RelativeTimeTemplates {
  min: (n: number) => string;
  hour: (n: number) => string;
  day: (n: number) => string;
}

const relativeTimeTemplates:
  & Record<Language, RelativeTimeTemplates>
  & Record<Exclude<Locale, Language>, RelativeTimeTemplates> = {
  ko: { min: (n) => `${n}분 전`, hour: (n) => `${n}시간 전`, day: (n) => `${n}일 전` },
  es: { min: (n) => `Hace ${n} min`, hour: (n) => `Hace ${n} h`, day: (n) => `Hace ${n} días` },
  en: { min: (n) => `${n} min ago`, hour: (n) => `${n} h ago`, day: (n) => `${n} days ago` },
  pt: { min: (n) => `Há ${n} min`, hour: (n) => `Há ${n} h`, day: (n) => `Há ${n} dias` },
  fr: { min: (n) => `Il y a ${n} min`, hour: (n) => `Il y a ${n} h`, day: (n) => `Il y a ${n} jours` },
  zh: { min: (n) => `${n}分钟前`, hour: (n) => `${n}小时前`, day: (n) => `${n}天前` },
  ja: { min: (n) => `${n}分前`, hour: (n) => `${n}時間前`, day: (n) => `${n}日前` },
  hi: { min: (n) => `${n} मिनट पहले`, hour: (n) => `${n} घंटे पहले`, day: (n) => `${n} दिन पहले` },
  de: { min: (n) => `vor ${n} Min.`, hour: (n) => `vor ${n} Std.`, day: (n) => `vor ${n} Tagen` },
  it: { min: (n) => `${n} min fa`, hour: (n) => `${n} h fa`, day: (n) => `${n} giorni fa` },
  ru: { min: (n) => `${n} мин назад`, hour: (n) => `${n} ч назад`, day: (n) => `${n} дн. назад` },
  ar: { min: (n) => `قبل ${n} دقيقة`, hour: (n) => `قبل ${n} ساعة`, day: (n) => `قبل ${n} يوم` },
  vi: { min: (n) => `${n} phút trước`, hour: (n) => `${n} giờ trước`, day: (n) => `${n} ngày trước` },
  th: { min: (n) => `${n} นาทีที่แล้ว`, hour: (n) => `${n} ชั่วโมงที่แล้ว`, day: (n) => `${n} วันที่แล้ว` },
  nl: { min: (n) => `${n} min geleden`, hour: (n) => `${n} u geleden`, day: (n) => `${n} dagen geleden` },
};

// #522 — Intl.DateTimeFormat 용 BCP-47 매핑 (기존 ko-KR/es-ES/en-US 값 보존).
const dateLocaleMap:
  & Record<Language, string>
  & Record<Exclude<Locale, Language>, string> = {
  ko: 'ko-KR',
  es: 'es-ES',
  en: 'en-US',
  pt: 'pt-BR',
  fr: 'fr-FR',
  zh: 'zh-CN',
  ja: 'ja-JP',
  hi: 'hi-IN',
  de: 'de-DE',
  it: 'it-IT',
  ru: 'ru-RU',
  ar: 'ar-SA',
  vi: 'vi-VN',
  th: 'th-TH',
  nl: 'nl-NL',
};

// 상대적 날짜 포맷팅 — #522: Language(3) → Locale(15) 전수 지원.
export function formatRelativeDate(date: Date, locale: Locale): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  const templates = relativeTimeTemplates[locale] ?? relativeTimeTemplates.es;

  if (diffMins < 60) {
    return templates.min(diffMins);
  } else if (diffHours < 24) {
    return templates.hour(diffHours);
  } else if (diffDays < 7) {
    return templates.day(diffDays);
  } else {
    // #1816: 절대 날짜는 매장 타임존(멕시코)으로 — 자정 부근 고객 기기 TZ off-by-one 차단.
    return new Intl.DateTimeFormat(
      dateLocaleMap[locale] ?? dateLocaleMap.es,
      { year: 'numeric', month: 'short', day: 'numeric', timeZone: DEFAULT_STORE_TIMEZONE }
    ).format(date);
  }
}
